/**
 * Gli avvisi del flusso, senza niente che disegni.
 *
 * Non è una campanella: gli avvisi che raccoglie esistono già nel flusso, e
 * finora si vedevano **solo entrando nella schermata giusta**. Sono quelli che
 * per definizione arrivano a qualcuno che sta guardando da un'altra parte —
 * l'**ordine finito** che aspetta il fine linea col carrello da venire a
 * prendere (regola 24), e l'**ordine fermo** perché manca merce da prelevare a
 * magazzino negozio (regola 15).
 *
 * Dal 02/09/2026 se ne aggiungono altri due che non vengono dalle code: il
 * **capo fallato** segnalato al banco (regola 8-bis), che resta acceso finché
 * l'ufficio non lo prende in carico, e lo **scarto di lavorazione** (regola
 * 20-quater), che resta acceso finché il capo perso non è rifatto. Quest'ultimo
 * è il solo modo di saperlo prima del fine linea, dove l'unica uscita è la
 * deroga sul parziale — cioè spedire meno di quanto il cliente ha comprato.
 *
 * Sta in un pacchetto e non dentro un'applicazione perché lo guardano in due:
 * il banco da un metro e l'ufficio da cinquanta centimetri. Quello che
 * condividono è la **regola**, non la campana; questo file non sa che esiste
 * una campana, e `dove` è una stringa perché non conosce nemmeno il router.
 *
 * ## Tre scelte
 *
 * **Le fonti seguono il ruolo.** Ogni sorgente è una rotta che la guardia può
 * rifiutare: un 403 non è un errore da mostrare, è «questa cosa non ti
 * riguarda», e quella sorgente semplicemente non contribuisce. Il magazziniere
 * non vede gli avvisi del fine linea, ed è giusto così.
 *
 * **Il numero non è mai finto.** Quando non si riesce a leggere, la risposta è
 * `null` — non la lista vuota. Uno zero che in realtà è «non lo so» è la
 * ragione per cui la gente smette di fidarsi dei badge.
 *
 * **Le fonti d'ufficio si chiedono, non si prendono da sé.** Fallati e scarti
 * sono facoltative, e non per prudenza: le due applicazioni hanno due router, e
 * `/quality` esiste solo in ufficio. Un amministratore che apre il banco
 * vedrebbe un avviso che porta a una rotta che lì non c'è — un 403 sulla
 * sorgente non basterebbe a evitarlo, perché lui quel permesso ce l'ha.
 *
 * Si accendono **insieme**, con un interruttore solo: sono le due metà della
 * stessa schermata, e due interruttori sarebbero due modi di sbagliarsi.
 */
import { useQuery } from '@tanstack/react-query';
import { useSessione } from '@errebi/sessione';
/**
 * Legge una rotta dell'API; `null` quando il ruolo non la può vedere.
 *
 * Il percorso è **intero** e non sotto `/flusso`: da quando c'è la sorgente dei
 * fallati le fonti non stanno più tutte nello stesso contesto, e un prefisso
 * cablato qui dentro sarebbe diventato la cosa da ricordarsi di non usare.
 */
async function leggi(percorso, token) {
    const r = await fetch(`/api${percorso}`, {
        headers: { authorization: `Bearer ${token}` },
    });
    // 403 = non è del tuo ruolo. 401 lo gestisce il giro della sessione.
    if (r.status === 403 || r.status === 401)
        return null;
    if (!r.ok)
        throw new Error(String(r.status));
    return r.json();
}
export function useAvvisi(opzioni = {}) {
    const { identita } = useSessione();
    const token = identita?.token ?? '';
    const conQualita = opzioni.ufficio === true;
    const q = useQuery({
        // L'opzione entra nella chiave: banco e ufficio girano nella stessa scheda
        // solo per sbaglio, ma due risposte diverse sotto la stessa chiave sarebbero
        // la campanella dell'uno mostrata all'altro.
        queryKey: ['notifiche', identita?.utente.login, conQualita],
        enabled: token !== '',
        // il flusso è condiviso: gli avvisi nascono da gesti di altri reparti
        refetchInterval: 15_000,
        queryFn: async () => {
            const [fineLinea, prelievi, fallati, scarti] = await Promise.all([
                leggi('/flusso/fine-linea', token).catch(() => null),
                leggi('/flusso/prelievi', token).catch(() => null),
                conQualita ? leggi('/fallati?aperti=1', token).catch(() => null) : null,
                conQualita ? leggi('/scarti?aperti=1', token).catch(() => null) : null,
            ]);
            // Nessuna sorgente leggibile: non lo sappiamo. Rispondere con la lista
            // vuota direbbe «niente da segnalare», che è la bugia che questo modulo
            // esiste per non dire — e che si scopre solo quando un avviso conta
            // davvero.
            //
            // Le fonti d'ufficio non entrano nel conto quando non sono state
            // chieste: una fonte spenta non è una fonte muta. E fra le due basta che
            // una risponda — un ruolo che vede gli scarti ma non i fallati esiste, ed
            // è la produzione.
            const qualitaMuta = fallati === null && scarti === null;
            if (fineLinea === null && prelievi === null && (!conQualita || qualitaMuta)) {
                return null;
            }
            const avvisi = [];
            for (const p of fineLinea?.daImballare ?? []) {
                avvisi.push({
                    id: `pronto:${p.ordine}`,
                    gravita: p.urgente ? 'urgente' : 'attesa',
                    titolo: `${p.cliente} · ordine ${p.ordine} è finito`,
                    dettaglio: `${p.capi} capi da imballare, carrello ${p.carrelli.join(', ') || '—'}.`,
                    dove: '/end-of-line',
                });
            }
            for (const f of prelievi?.daPrelevare ?? []) {
                avvisi.push({
                    id: `fermo:${f.ordine}`,
                    gravita: f.urgente ? 'urgente' : 'attesa',
                    titolo: `${f.cliente} · ordine ${f.ordine} è fermo`,
                    dettaglio: 'manca ' +
                        f.righe.map((r) => `${r.articolo} ${r.colore} ×${r.capi}`).join(', ') +
                        ', da prelevare a magazzino negozio.',
                    dove: '/picking',
                });
            }
            for (const f of fallati ?? []) {
                avvisi.push({
                    id: `fallato:${f.id}`,
                    // Mai urgente: il capo è già fuori dalla cassetta e il PO è già
                    // riaperto: nessuno sta aspettando fermo, e un rosso qui toglierebbe
                    // peso ai due avvisi che invece bloccano una spedizione.
                    gravita: 'attesa',
                    titolo: `Fallato · acquisto ${f.ordine} riga ${f.riga}`,
                    dettaglio: `${f.quantita} capo/i ${f.articolo} ${f.colore}, taglia ${f.taglia} — ` +
                        `segnalati da ${f.chi}.`,
                    dove: '/quality',
                });
            }
            for (const sc of scarti ?? []) {
                avvisi.push({
                    id: `scarto:${sc.id}`,
                    // Mai urgente, come il fallato: il capo è già perso e nessuno sta
                    // fermo ad aspettarlo. Ma è la cosa che a fine linea diventerebbe una
                    // spedizione incompleta, e per questo va detta adesso.
                    gravita: 'attesa',
                    titolo: `${sc.cliente} · ordine ${sc.ordine} ha perso ${sc.capi} capo/i`,
                    dettaglio: `${sc.articolo} ${sc.colore}, taglia ${sc.taglia}, in ${sc.reparto} — ` +
                        `${sc.causale}, ${sc.chi}. Va reintegrato.`,
                    dove: '/quality',
                });
            }
            // gli urgenti in cima, come nelle code di reparto
            return avvisi.sort((a, b) => Number(b.gravita === 'urgente') - Number(a.gravita === 'urgente'));
        },
    });
    // `null` = non lo sappiamo. Diverso da «nessun avviso», e sono i due casi che
    // chi rende deve mostrare in modo diverso.
    return { avvisi: q.isSuccess ? q.data : null };
}
//# sourceMappingURL=alerts.js.map